/* eslint-disable max-classes-per-file */
import React, { Component } from 'react'
import {
  TmrItem,
  ProductInfoField,
  TmrFullItemEvent,
  AttributeMappings,
  TmrSimpleAttributesMap,
  ItemEvents,
  Items,
  IdentifierType
} from 'stylewhere/api'
import { Spinner } from '@chakra-ui/react'
import {
  Box,
  Card,
  IdentifierRow,
  Modal,
  ProductInfoBox,
  Spacer,
  ItemEventBox,
  ModalTitle,
  ItemComponentRow,
  ItemAttributesCard
} from 'stylewhere/components'
import { T, __ } from 'stylewhere/i18n'

interface Props {
  onClose: () => void
  item?: TmrItem
  itemId?: string
  visible: boolean
  hideIdentifiersByType?: IdentifierType[]
  overrideProductInfoFields?: ProductInfoField[]
  sortIdentifiersBy?: string[]
  showIdentifierStatus?: boolean
  excludeAttributeMappings?: string[]
  skipIdentifierFiltering?: boolean
  customItemAttributes?: (item?: TmrItem) => TmrSimpleAttributesMap[]
  readTid?: string
  retrieveTid?: string
}

interface State {
  item?: TmrItem
  events?: TmrFullItemEvent[]
  fields?: ProductInfoField[]
  attributes?: TmrSimpleAttributesMap[]
  componentAttributes?: TmrSimpleAttributesMap[][]
  loader: boolean
  viewProductAttribute: boolean
}

export class ItemInfoModal extends Component<Props, State> {
  state: State = {
    item: undefined,
    events: undefined,
    fields: undefined,
    attributes: undefined,
    componentAttributes: undefined,
    loader: true,
    viewProductAttribute: false
  }

  productFields: ProductInfoField[] = [
    { path: 'color.value', label: __(T.misc.color) },
    { path: 'material.value', label: __(T.misc.material) },
    { path: 'size.value', label: __(T.misc.size) },
    { path: 'style.value', label: __(T.misc.style) }
  ]

  componentDidMount() {
    void this.init()
  }

  fetchItemForInit = async (): Promise<TmrItem | undefined> => {
    const { item, itemId } = this.props
    if (item) return item
    if (itemId) return Items.get<TmrItem>(itemId).then((res) => res)
    return undefined
  }

  mergeCustomAttributesForItem = (
    attrs: TmrSimpleAttributesMap[],
    ret: TmrItem
  ): TmrSimpleAttributesMap[] => {
    const { customItemAttributes } = this.props
    if (!customItemAttributes) return attrs
    const customAttrs = customItemAttributes(ret)
    if (!customAttrs?.length) return attrs
    return [...customAttrs, ...attrs]
  }

  fetchEventsAndOptionalComponents = async (
    ret: TmrItem
  ): Promise<{ eventsContent: TmrFullItemEvent[]; componentAttributes?: TmrSimpleAttributesMap[][] }> => {
    try {
      const eventsRes = await ItemEvents.get(ret.id, {
        size: 100,
        sort: 'creationDate,DESC'
      })
      const eventsContent = eventsRes?.content ?? []
      if (!ret.components?.length) {
        return { eventsContent }
      }
      const componentAttributes = await AttributeMappings.getItemComponentAttributes(ret)
      return { eventsContent, componentAttributes }
    } catch {
      return { eventsContent: [] }
    }
  }

  init = async () => {
    const ret = await this.fetchItemForInit()
    if (!ret?.id) return

    const excludeParams = this.props.excludeAttributeMappings || ['barcodeBC']
    const cols: ProductInfoField[] = await AttributeMappings.getProductAttribute(
      ret.product,
      this.productFields,
      excludeParams
    )
    const attrs = this.mergeCustomAttributesForItem(await AttributeMappings.getItemAttribute(ret), ret)
    const { eventsContent, componentAttributes } = await this.fetchEventsAndOptionalComponents(ret)

    this.setState({
      fields: cols,
      attributes: attrs,
      item: ret,
      events: eventsContent,
      loader: false,
      ...(componentAttributes ? { componentAttributes } : {})
    })
  }

  getProductFields = () => {
    const { overrideProductInfoFields } = this.props
    const { fields, viewProductAttribute } = this.state
    if (overrideProductInfoFields) {
      return overrideProductInfoFields
    }
    if (viewProductAttribute) {
      return fields || this.productFields
    }
    return this.productFields
  }

  managerViewProductAttribute = () => {
    this.setState((prevState) => ({ viewProductAttribute: !prevState.viewProductAttribute }))
  }

  getItemComponentKey = (itemComponent: TmrSimpleAttributesMap[]) =>
    itemComponent.map((attribute) => `${attribute.label}:${String(attribute.value)}`).join('|')

  render() {
    const {
      visible,
      onClose,
      hideIdentifiersByType,
      showIdentifierStatus,
      sortIdentifiersBy,
      skipIdentifierFiltering,
      readTid,
      retrieveTid
    } = this.props
    const { attributes, events, loader, viewProductAttribute, item, componentAttributes } = this.state
    let filteredIdentifiers = []

    if (item?.identifiers) {
      if (skipIdentifierFiltering) {
        filteredIdentifiers = [...item.identifiers]
      } else {
        filteredIdentifiers = item.identifiers
          .filter((idf) => idf.status !== 'REPLACED_ASSOCIATION')
          .filter((idf) => !hideIdentifiersByType?.includes(idf.type))
      }
      if (sortIdentifiersBy?.length) {
        filteredIdentifiers = [...filteredIdentifiers].sort((a, b) => {
          const aIndex = sortIdentifiersBy.indexOf(a.role)
          const bIndex = sortIdentifiersBy.indexOf(b.role)
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
          if (aIndex !== -1) return -1
          if (bIndex !== -1) return 1
          return 0
        })
      }
    }

    return (
      <Modal size="6xl" onClose={onClose} visible={visible} fullContent showCloseButton>
        <Box flex row>
          <Box flex={3} ml={35} style={{ minWidth: 0 }}>
            <ModalTitle
              title={__(T.misc.item_info)}
              data-testid={`identifiers-modal-title`}
              style={{ flex: 0, paddingTop: 20, paddingBottom: 15 }}
            />
            <Box
              flex
              ph={35}
              pb={0}
              pr={0}
              pl={0}
              mr={35}
              mb={20}
              style={{
                height: 'calc(80vh - 40px)',
                maxHeight: 'calc(80vh - 40px)',
                overflow: 'auto',
                overflowX: 'hidden'
              }}
            >
              {!loader && item && (
                <>
                  <ProductInfoBox
                    product={item.product}
                    fields={this.getProductFields()}
                    showMore={loader ? undefined : () => this.managerViewProductAttribute()}
                    viewMore={viewProductAttribute}
                    paddingCard={'0px 15px 0px 0px'}
                    shadowCard={'unset'}
                  />
                  <Spacer />
                  <ItemAttributesCard attributes={attributes} labels={item.labels} />
                  <Card title={__(T.misc.identifiers)} titleMarginBottom={'10px'}>
                    {filteredIdentifiers.map((identifier, index) => (
                      <IdentifierRow
                        key={identifier.type}
                        identifier={identifier}
                        last={index === filteredIdentifiers.length - 1}
                        showStatus={showIdentifierStatus}
                        readTid={readTid}
                        retrieveTid={retrieveTid}
                      />
                    ))}
                  </Card>
                  {componentAttributes && componentAttributes.length > 0 && (
                    <Card title={__(T.fields.item_components)}>
                      {componentAttributes.map((component, index) => (
                        <ItemComponentRow
                          key={this.getItemComponentKey(component)}
                          itemComponent={component}
                          last={index === componentAttributes.length - 1}
                        />
                      ))}
                    </Card>
                  )}
                </>
              )}
              {loader && (
                <Box mt={20} center>
                  <Spinner color="#000" size="xl" />
                </Box>
              )}
            </Box>
          </Box>
          <ItemEventBox events={events} />
        </Box>
      </Modal>
    )
  }
}
